import { useCallback, useEffect, useState } from 'react'

import type {
  Collection,
  DQLResult,
  UseCollectionsParams,
  UseCollectionsReturn,
} from '../types'

/**
 * Hook to fetch and manage collections from the Ditto database
 *
 * This hook replaces the `useCollections` hook from `@dittolive/react-ditto`
 * by using DQL queries instead of the Ditto SDK websocket connection.
 *
 * @example
 * ```typescript
 * const { executeDqlQuery } = useRPCHook({ appId, cloudUrl })
 * const { collections, isLoading, refetch } = useCollections({
 *   executeDqlQuery,
 *   sort: { propertyPath: 'name', direction: 'ascending' }
 * })
 * ```
 */
export function useCollections({
  executeDqlQuery,
  sort,
  shouldAutoFetch = true,
}: UseCollectionsParams): UseCollectionsReturn {
  const [collections, setCollections] = useState<Collection[]>([])
  const [isFetchingCollections, setIsFetchingCollections] =
    useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  // Extract primitives so callers can pass inline sort objects without causing infinite re-renders
  const { propertyPath: sortPropertyPath, direction: sortDirection } =
    sort ?? {}

  const fetchCollections = useCallback(async () => {
    setIsFetchingCollections(true)
    setError(null)

    try {
      // Build sort clause
      const sortClause = sortPropertyPath
        ? `ORDER BY ${sortPropertyPath} ${sortDirection === 'ascending' ? 'ASC' : 'DESC'}`
        : 'ORDER BY name ASC'

      const response = await executeDqlQuery(
        `SELECT * FROM __collections ${sortClause}`,
      )

      // Check for DQL errors
      if (response.error && 'description' in response.error) {
        throw new Error(response.error.description)
      }

      // Transform DQL results to Collection type
      const transformedCollections: Collection[] = response.items.map(
        (item: DQLResult) => ({
          _id: item._id as string,
          name: item.name as string,
        }),
      )

      setCollections(transformedCollections)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch collections'
      setError(new Error(errorMessage))
    } finally {
      setIsFetchingCollections(false)
    }
  }, [executeDqlQuery, sortPropertyPath, sortDirection])

  // Auto-fetch collections when shouldAutoFetch is true and we haven't initialized yet
  useEffect(() => {
    if (shouldAutoFetch) {
      fetchCollections()
    }
    // We intentionally don't set loading to false when shouldAutoFetch is false
    // This prevents the "flash" of empty state while waiting for dependencies
  }, [shouldAutoFetch, fetchCollections])

  return {
    collections,
    isFetchingCollections,
    error,
    refetch: fetchCollections,
  }
}
